import { defineStore } from 'pinia'
import { ref, computed } from 'vue'
import { askIQ, askIQStream } from '../../../account/src/api/askIQApi'

export const useIQChatStore = defineStore('iqChat', () => {
  // State
  const messages = ref([])
  const isLoading = ref(false)
  const isGenerating = ref(false)
  const isThinking = ref(false)  // New: tracks when AI is in "thinking" phase
  const currentReasoning = ref('')  // New: stores the reasoning/thinking text
  const currentResponse = ref('')
  let abortController = null

  // Getters
  const hasMessages = computed(() => messages.value.length > 0)
  const lastMessage = computed(() => messages.value[messages.value.length - 1])

  // Actions
  const addUserMessage = (message) => {
    messages.value.push({
      id: Date.now(),
      type: 'user',
      content: message,
      timestamp: new Date()
    })
  }

  const addAIMessage = (message) => {
    messages.value.push({
      id: Date.now(),
      type: 'ai',
      content: message,
      timestamp: new Date()
    })
  }

  const startGenerating = () => {
    isGenerating.value = true
    isLoading.value = true
    currentResponse.value = ''
    currentReasoning.value = ''
  }

  const startThinking = () => {
    isThinking.value = true
    currentReasoning.value = ''
  }

  const stopThinking = () => {
    isThinking.value = false
  }

  const updateCurrentReasoning = (reasoning) => {
    currentReasoning.value = reasoning
  }

  const stopGenerating = () => {
    // Cancel any ongoing API request
    if (abortController) {
      abortController.abort()
      abortController = null
    }
    
    // Add AI message BEFORE setting isGenerating to false
    // This ensures watchers see the message when they check for suggestions
    if (currentResponse.value) {
      addAIMessage(currentResponse.value)
      currentResponse.value = ''
    }
    
    isGenerating.value = false
    isLoading.value = false
    isThinking.value = false
    currentReasoning.value = ''
    // If stopped during loading (no response yet), question remains without answer
  }

  const updateCurrentResponse = (response) => {
    currentResponse.value = response
  }

  const clearChat = () => {
    // Cancel any ongoing API request
    if (abortController) {
      abortController.abort()
      abortController = null
    }
    
    messages.value = []
    currentResponse.value = ''
    currentReasoning.value = ''
    isGenerating.value = false
    isLoading.value = false
    isThinking.value = false
  }

  const generateRandomResponse = async (userMessage) => {
    // Start with loading state (thinking period)
    isLoading.value = true
    isGenerating.value = false
    currentResponse.value = ''
    
    // Wait 2-3 seconds before starting to type (thinking time)
    await new Promise(resolve => setTimeout(resolve, 2000 + Math.random() * 1000))
    
    if (!isLoading.value) return // Stop if user clicked stop during thinking
    
    // Switch to generating state
    isLoading.value = false
    isGenerating.value = true
    
    // Simulate AI thinking and generating response with longer content
    const responses = [
      "I understand your question about that topic. Let me help you with some comprehensive insights that should clarify things for you. Based on my analysis, there are several key points to consider. First, the foundational concepts are crucial to understand. Second, the practical applications can vary significantly. Third, the long-term implications should not be overlooked. Let me break this down further for you.",
      "That's an interesting point you've raised. Based on my analysis, I would suggest a multi-faceted approach to this problem. The solution involves understanding the core principles, examining the current landscape, and identifying potential opportunities. There are several strategies we can explore together. Each approach has its own merits and considerations that we should discuss in detail.",
      "I can see you're looking for information on this subject. Here's what I can tell you based on my knowledge and experience. This is a complex topic that requires careful consideration of multiple factors. The key is to understand the underlying mechanisms and how they interact with each other. There are several approaches we can take, each with its own advantages and potential challenges.",
      "Great question! This is a complex topic, but I'll break it down for you in a way that's easy to understand. The fundamental concepts are important to grasp first. Then we can explore the practical applications and real-world implications. There are several key areas we should focus on, and each one contributes to the overall understanding. Let me provide you with a comprehensive overview.",
      "I appreciate you asking about this. Let me provide you with a comprehensive answer that covers the essential aspects. This topic involves multiple interconnected elements that work together to create the overall system. Understanding these relationships is crucial for making informed decisions. There are several perspectives to consider, and each offers valuable insights into the bigger picture.",
      "That's a thoughtful question. From my perspective, here are the key points that should be addressed. The first consideration is the foundational understanding of the concepts involved. Second, we need to examine the practical implications and real-world applications. Third, it's important to consider the long-term effects and potential outcomes. Each of these aspects contributes to a complete understanding of the topic.",
      "I'm glad you brought this up. This is something that many people wonder about, and it's a great question to explore. The answer involves several interconnected factors that work together in complex ways. Understanding these relationships is key to grasping the full picture. There are multiple approaches we can take to address this, and each offers unique insights into the topic at hand.",
      "Excellent question! Let me share some insights that might be helpful for your understanding. This is a multifaceted topic that requires us to consider various angles and perspectives. The key is to understand how different elements interact and influence each other. There are several important factors to keep in mind, and each one plays a crucial role in the overall system."
    ]
    
    const randomResponse = responses[Math.floor(Math.random() * responses.length)]
    
    // Simulate typing effect (ChatGPT-like behavior - word by word)
    const words = randomResponse.split(' ')
    let typedResponse = ''
    
    for (let i = 0; i < words.length; i++) {
      if (!isGenerating.value) break // Stop if user clicked stop
      
      // Add word with space (except for the last word)
      typedResponse += words[i] + (i < words.length - 1 ? ' ' : '')
      
      // During typing, only show the answer text (no sources for random responses)
      updateCurrentResponse({
        answer: typedResponse,
        sources: [],
        version: '1.0.0'
      })
      
      // ChatGPT-like speed: 3-4 words at a time with variable delay
      const wordsPerBatch = Math.random() < 0.3 ? 4 : 3 // 30% chance for 4 words, 70% for 3 words
      if ((i + 1) % wordsPerBatch === 0 || i === words.length - 1) {
        await new Promise(resolve => setTimeout(resolve, 50 + Math.random() * 100))
      }
    }
    
    stopGenerating()
  }

  // Fallback reasoning templates for when streaming is not available
  const generateFallbackReasoning = () => {
    const templates = [
      `### Analyzing Your Query\n\nI'm examining the key aspects of your question to provide the most relevant and accurate response.\n\n**Understanding Context:**\n* Identifying the main subject of your inquiry\n* Considering related concepts and dependencies\n* Evaluating the scope of information needed`,
      
      `### Reasoning Process\n\n**Step 1: Query Analysis**\nBreaking down your question to understand the core requirements and context.\n\n**Step 2: Information Gathering**\nRetrieving relevant data points and connecting related concepts.`,
      
      `### Thought Process\n\n**Initial Assessment:**\nEvaluating the nature of your request and determining the best approach.\n\n**Key Considerations:**\n* Relevance to your current context\n* Accuracy of information`
    ]
    return templates[Math.floor(Math.random() * templates.length)]
  }

  const generateAIResponse = async (userMessage, k = 5, customEndpoint = null, customPayload = null) => {
    try {
      // Start with loading state and thinking animation
      isLoading.value = true
      isGenerating.value = false
      isThinking.value = true
      currentResponse.value = ''
      currentReasoning.value = ''
      
      // Create abort controller for this request
      abortController = new AbortController()
      
      let streamingStarted = false
      let fullResponse = null
      
      // Try to use streaming API first
      try {
        const response = await askIQStream({
          question: userMessage,
          k: k,
          signal: abortController.signal,
          customEndpoint: customEndpoint,
          customPayload: customPayload,
          onThinkingUpdate: (thinkingText) => {
            // Update reasoning in real-time as it streams from backend
            if (isThinking.value) {
              streamingStarted = true
              currentReasoning.value = thinkingText
            }
          },
          onComplete: (completeData) => {
            fullResponse = completeData
          }
        })
        
        // If streaming didn't provide data, use the final response
        if (!fullResponse && response.data) {
          fullResponse = response.data
        }
      } catch (streamError) {
        // If streaming fails, fall back to regular API
        if (streamError.name === 'AbortError' || streamError.name === 'CanceledError') {
          throw streamError
        }
        
        console.warn('Streaming failed, falling back to regular API:', streamError)
        
        // Show fallback reasoning while waiting
        if (!streamingStarted) {
          const fallbackReasoning = generateFallbackReasoning()
          const reasoningChars = fallbackReasoning.split('')
          let typedReasoning = ''
          const chunkSize = 3
          
          for (let i = 0; i < reasoningChars.length; i += chunkSize) {
            if (!isThinking.value) break
            typedReasoning += reasoningChars.slice(i, i + chunkSize).join('')
            currentReasoning.value = typedReasoning
            await new Promise(resolve => setTimeout(resolve, 20 + Math.random() * 30))
          }
        }
        
        // Fall back to regular API
        const response = await askIQ({
          question: userMessage,
          k: k,
          signal: abortController.signal,
          customEndpoint: customEndpoint,
          customPayload: customPayload
        })
        
        fullResponse = response.data
      }
      
      if (!isLoading.value) return // Stop if user clicked stop during API call
      
      // Get the complete response from API
      fullResponse = fullResponse || {}
      
      // Use the accumulated reasoning from streaming or the response reasoning
      const reasoning = currentReasoning.value || fullResponse.reasoning || ''
      
      // Brief pause after reasoning is complete
      await new Promise(resolve => setTimeout(resolve, 300))
      
      // Thinking phase complete, switch to generating state
      isThinking.value = false
      isLoading.value = false
      isGenerating.value = true
      
      // Determine suggestion type from the payload
      const suggestionType = customPayload?.suggestion_type || null
      
      // Check if this is a suggestions response
      let aiResponse = ''
      let suggestions = null
      let actions = []
      
      if (fullResponse.suggestions && Array.isArray(fullResponse.suggestions)) {
        const suggestionCount = fullResponse.suggestions.length
        
        // Check if suggestions is empty
        if (suggestionCount === 0) {
          if (suggestionType === 'responsibility_checkpoint_criteria') {
            aiResponse = 'I could not find any new checkpoint suggestions. Try providing more specific context.'
          } else {
            aiResponse = 'I could not find any new Responsibility suggestions for this role. The role may already have comprehensive responsibilities attached, or try providing more specific context.'
          }
        } else {
          // Use message from API response if available, otherwise fallback
          if (fullResponse.message) {
            aiResponse = fullResponse.message
          } else {
            // Fallback message based on suggestion type
            if (suggestionType === 'responsibility_checkpoint_criteria') {
              aiResponse = `I suggested ${suggestionCount} new ${suggestionCount === 1 ? 'checkpoint' : 'checkpoints'} that you can review in the right pane, one checkpoint at a time.`
            } else {
              aiResponse = `I suggested ${suggestionCount} new ${suggestionCount === 1 ? 'Responsibility' : 'Responsibilities'} that you can review, one Responsibility at a time:`
            }
          }
          
          suggestions = fullResponse.suggestions
          
          // Add action button - type differs based on suggestion type
          actions = [
            {
              label: "Refresh Suggestions",
              type: suggestionType === 'responsibility_checkpoint_criteria' ? "refresh-checkpoint-suggestions" : "review-suggestions"
            }
          ]
        }
      } else if (fullResponse.instructions && fullResponse.instructions.steps && Array.isArray(fullResponse.instructions.steps)) {
        // This is a video instructions response (validation PASS)
        const stepCount = fullResponse.instructions.steps.length
        
        // Use message from API or generate default
        aiResponse = fullResponse.message || `I analyzed the video and created ${stepCount} inspection ${stepCount === 1 ? 'step' : 'steps'}. Review them in the right pane.`
        
        // Map steps to the format expected by MicrolearningViewEdit/InstructionStepCard
        // Ensure competence_category is lowercase and uses British spelling as expected by the component validator
        const mapCompetenceCategory = (category) => {
          const normalized = (category || 'ability').toLowerCase()
          // Convert American "behavior" to British "behaviour" for the component validator
          return normalized === 'behavior' ? 'behaviour' : normalized
        }
        
        const mappedSteps = fullResponse.instructions.steps.map(step => ({
          competence_category: mapCompetenceCategory(step.competence_category),
          inspector_action: step.inspector_action || '',
          look_for: step.look_for || '',
          pass_criteria: step.pass_criteria || '',
          fail_criteria: step.fail_criteria || '',
          timestamp_ref: step.timestamp_ref || '',
          video_jump_url: step.video_jump_url || ''
        }))
        
        // Store instruction data for the right pane
        suggestions = {
          steps: mappedSteps,
          summary: fullResponse.instructions.overall_rubric || null,
          error: null,
          video_url: fullResponse.video_url || null,
          video_title: fullResponse.instructions.video_title || null
        }
      } else if (fullResponse.validation && fullResponse.validation.status === 'FAIL') {
        // Video validation failed - show error message
        aiResponse = 'This video does not match the context of the checkpoint!'
        
        // Store error data for the right pane
        suggestions = {
          steps: [],
          summary: null,
          error: fullResponse.validation.reasoning || 'Video validation failed.',
          video_url: fullResponse.video_url || null
        }
      } else if (fullResponse.error) {
        // Video is not valid for context
        aiResponse = fullResponse.error
      } else {
        aiResponse = fullResponse.answer || fullResponse.response || 'I apologize, but I could not generate a response at this time.'
      }
      
      // Simulate typing effect for the API response (ChatGPT-like behavior - word by word)
      const words = aiResponse.split(' ')
      let typedResponse = ''
      
      for (let i = 0; i < words.length; i++) {
        if (!isGenerating.value) break // Stop if user clicked stop
        
        // Add word with space (except for the last word)
        typedResponse += words[i] + (i < words.length - 1 ? ' ' : '')
        
        // During typing, only show the answer text (no sources/actions yet)
        updateCurrentResponse({
          answer: typedResponse,
          sources: [], // No sources during typing
          version: fullResponse.version || '1.0.0',
          suggestions: null,
          suggestionType: null,
          role_name: null,
          reasoning: reasoning, // Keep reasoning visible during typing
          responsibility_name: null,
          actions: [] // No actions during typing
        })
        
        // ChatGPT-like speed: 3-4 words at a time with variable delay
        const wordsPerBatch = Math.random() < 0.3 ? 4 : 3 // 30% chance for 4 words, 70% for 3 words
        if ((i + 1) % wordsPerBatch === 0 || i === words.length - 1) {
          await new Promise(resolve => setTimeout(resolve, 50 + Math.random() * 100))
        }
      }
      
      // After typing is complete, show the full response with sources, actions, and suggestions
      if (isGenerating.value) {
        const finalResponse = {
          answer: aiResponse,
          sources: fullResponse.sources || [],
          version: fullResponse.version || '1.0.0',
          suggestions: suggestions,
          suggestionType: suggestionType,
          role_name: fullResponse.role_name || null,
          reasoning: reasoning,
          responsibility_name: fullResponse.responsibility_name || null,
          actions: actions
        };
        updateCurrentResponse(finalResponse);
      }
      stopGenerating()
    } catch (error) {
      // Don't show error if request was intentionally aborted
      if (error.name === 'AbortError' || error.name === 'CanceledError') {
        console.log('Request cancelled by user')
        isLoading.value = false
        isGenerating.value = false
        isThinking.value = false
        currentReasoning.value = ''
        return
      }
      
      console.error('Error generating AI response:', error)
      isLoading.value = false
      isGenerating.value = false
      isThinking.value = false
      currentReasoning.value = ''
      
      // Add error message
      const errorMessage = 'I apologize, but I encountered an error while processing your request. Please try again.'
      addAIMessage(errorMessage)
    }
  }

  const generateCustomResponse = async (customResponseText, actions = []) => {
    try {
      // Start with loading state (thinking period)
      isLoading.value = true
      isGenerating.value = false
      currentResponse.value = ''
      
      // Wait 1-2 seconds before starting to type (thinking time)
      await new Promise(resolve => setTimeout(resolve, 1000 + Math.random() * 1000))
      
      if (!isLoading.value) return // Stop if user clicked stop during thinking
      
      // Switch to generating state
      isLoading.value = false
      isGenerating.value = true
      
      // Simulate typing effect (ChatGPT-like behavior - word by word)
      const words = customResponseText.split(' ')
      let typedResponse = ''
      
      for (let i = 0; i < words.length; i++) {
        if (!isGenerating.value) break // Stop if user clicked stop
        
        // Add word with space (except for the last word)
        typedResponse += words[i] + (i < words.length - 1 ? ' ' : '')
        
        // During typing, only show the answer text (no actions yet)
        updateCurrentResponse({
          answer: typedResponse,
          sources: [],
          actions: [],
          version: '1.0.0'
        })
        
        // ChatGPT-like speed: 3-4 words at a time with variable delay
        const wordsPerBatch = Math.random() < 0.3 ? 4 : 3
        if ((i + 1) % wordsPerBatch === 0 || i === words.length - 1) {
          await new Promise(resolve => setTimeout(resolve, 50 + Math.random() * 100))
        }
      }
      
      // After typing is complete, show actions
      if (isGenerating.value && actions.length > 0) {
        updateCurrentResponse({
          answer: customResponseText,
          sources: [],
          actions: actions,
          version: '1.0.0'
        })
      }
      
      stopGenerating()
    } catch (error) {
      console.error('Error generating custom response:', error)
      isLoading.value = false
      isGenerating.value = false
      
      // Add error message
      const errorMessage = 'I apologize, but I encountered an error while processing your request.'
      addAIMessage(errorMessage)
    }
  }

  return {
    // State
    messages,
    isLoading,
    isGenerating,
    isThinking,
    currentResponse,
    currentReasoning,
    
    // Getters
    hasMessages,
    lastMessage,
    
    // Actions
    addUserMessage,
    addAIMessage,
    startGenerating,
    stopGenerating,
    startThinking,
    stopThinking,
    updateCurrentResponse,
    updateCurrentReasoning,
    clearChat,
    generateRandomResponse,
    generateAIResponse,
    generateCustomResponse
  }
})
